export function escapeHtml(text) {
	const d = document.createElement('div');
	d.textContent = text == null ? '' : String(text);
	return d.innerHTML;
}

export function formatMoney(amount, currency) {
	const n = Number(amount) || 0;
	try {
		return new Intl.NumberFormat(undefined, {
			style: 'currency',
			currency: currency || 'USD',
		}).format(n);
	} catch (e) {
		return (currency || 'USD') + ' ' + n.toFixed(2);
	}
}

export function parseQueryServiceId() {
	const params = new URLSearchParams(window.location.search);
	const id = parseInt(
		params.get('service_id') || params.get('bookpress_service') || '0',
		10
	);
	return id > 0 ? id : 0;
}

export function parseQueryStaffId() {
	const params = new URLSearchParams(window.location.search);
	const id = parseInt(params.get('staff_id') || '0', 10);
	return id > 0 ? id : 0;
}

/**
 * Build booking page URL with query params.
 */
export function buildBookingUrl(baseUrl, params) {
	if (!baseUrl) {
		return '';
	}
	try {
		const url = new URL(baseUrl, window.location.origin);
		Object.keys(params || {}).forEach((key) => {
			const val = params[key];
			if (val !== undefined && val !== null && val !== '') {
				url.searchParams.set(key, String(val));
			}
		});
		return url.toString();
	} catch (e) {
		const sep = baseUrl.indexOf('?') >= 0 ? '&' : '?';
		const qs = Object.keys(params || {})
			.filter((k) => params[k] != null && params[k] !== '')
			.map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(String(params[k])))
			.join('&');
		return qs ? baseUrl + sep + qs : baseUrl;
	}
}

export function getInitials(name) {
	return String(name || '')
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.map((part) => part.charAt(0))
		.join('')
		.slice(0, 2)
		.toUpperCase();
}

export function getServicePrice(service, staff) {
	if (!service) {
		return 0;
	}
	if (staff && staff.price_override != null && staff.price_override !== '') {
		return Number(staff.price_override) || 0;
	}
	return Number(service.base_price) || 0;
}

function formatDateBySetting(ms, tz, dateFormat) {
	if (dateFormat === 'MM/DD/YYYY') {
		return new Intl.DateTimeFormat(undefined, {
			timeZone: tz,
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		}).format(new Date(ms));
	}
	if (dateFormat === 'YYYY-MM-DD') {
		return new Intl.DateTimeFormat('en-CA', {
			timeZone: tz,
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		}).format(new Date(ms));
	}
	return new Intl.DateTimeFormat(undefined, {
		timeZone: tz,
		weekday: 'long',
		month: 'short',
		day: 'numeric',
	}).format(new Date(ms));
}

export function formatShortDateLabel(dateStr, options = {}) {
	if (!dateStr) {
		return '';
	}
	const [y, m, d] = dateStr.split('-').map(Number);
	const tz = options.timezone || options.timeZone;
	if (tz) {
		const ms = wallTimeToUtcMs(dateStr, '12:00', tz);
		return formatDateBySetting(ms, tz, options.dateFormat || options.date_format);
	}
	const dt = new Date(y, m - 1, d);
	return dt.toLocaleDateString(undefined, {
		weekday: 'long',
		month: 'short',
		day: 'numeric',
	});
}

/**
 * Today's calendar date (YYYY-MM-DD) in the given IANA timezone.
 */
export function todayInTimezone(timeZone) {
	if (!timeZone) {
		const t = new Date();
		return (
			t.getFullYear() +
			'-' +
			String(t.getMonth() + 1).padStart(2, '0') +
			'-' +
			String(t.getDate()).padStart(2, '0')
		);
	}
	return new Intl.DateTimeFormat('en-CA', {
		timeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
	}).format(new Date());
}

/**
 * UTC instant for a wall-clock date/time in an IANA timezone.
 */
export function wallTimeToUtcMs(dateStr, timeStr, timeZone) {
	const [y, m, d] = dateStr.split('-').map(Number);
	const [hh, mm] = String(timeStr || '00:00')
		.split(':')
		.map((part) => parseInt(part, 10) || 0);

	const formatter = new Intl.DateTimeFormat('en-US', {
		timeZone,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});

	const read = (parts, type) =>
		parseInt(parts.find((p) => p.type === type).value, 10);

	let utc = Date.UTC(y, m - 1, d, hh, mm, 0);

	for (let i = 0; i < 6; i++) {
		const parts = formatter.formatToParts(new Date(utc));
		const zy = read(parts, 'year');
		const zm = read(parts, 'month');
		const zd = read(parts, 'day');
		const zh = read(parts, 'hour');
		const zmin = read(parts, 'minute');

		const diffMin =
			(y - zy) * 525600 +
			(m - zm) * 43200 +
			(d - zd) * 1440 +
			(hh - zh) * 60 +
			(mm - zmin);

		if (diffMin === 0) {
			break;
		}

		utc += diffMin * 60000;
	}

	return utc;
}

export function couponDiscountLabel(coupon, currency) {
	if (!coupon || !coupon.valid) {
		return '';
	}
	if (coupon.discount_type === 'percentage') {
		return '-' + String(coupon.discount_value) + '% discount applied';
	}
	return (
		'-' + formatMoney(coupon.discount_amount, currency || 'USD') + ' discount applied'
	);
}

/**
 * Live date/time string in the configured IANA timezone.
 */
export function formatLiveDateTime(options = {}) {
	const tz = options.timezone || options.timeZone;
	if (!tz) {
		return new Date().toLocaleString();
	}
	const hour12 = options.hour12 !== false;
	return new Intl.DateTimeFormat(undefined, {
		timeZone: tz,
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit',
		hour12,
	}).format(new Date());
}

export function formatDateLabel(dateStr, options = {}) {
	if (!dateStr) {
		return '';
	}
	const tz = options.timezone || options.timeZone;
	if (tz) {
		const ms = wallTimeToUtcMs(dateStr, '12:00', tz);
		return new Intl.DateTimeFormat(undefined, {
			timeZone: tz,
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		}).format(new Date(ms));
	}
	const [y, m, d] = dateStr.split('-').map(Number);
	const dt = new Date(y, m - 1, d);
	return dt.toLocaleDateString(undefined, {
		weekday: 'long',
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
}

export function formatTimeLabel(timeStr, dateStr, options = {}) {
	if (!timeStr) {
		return '';
	}

	const tz = options.timezone || options.timeZone;
	const hour12 = options.hour12 !== false;

	if (tz) {
		const date = dateStr || options.date || todayInTimezone(tz);
		const ms = wallTimeToUtcMs(date, timeStr, tz);
		return new Intl.DateTimeFormat(undefined, {
			timeZone: tz,
			hour: 'numeric',
			minute: '2-digit',
			hour12,
		}).format(new Date(ms));
	}

	const [h, min] = timeStr.split(':').map(Number);
	const dt = new Date();
	dt.setHours(h, min || 0, 0, 0);
	return dt.toLocaleTimeString(undefined, {
		hour: 'numeric',
		minute: '2-digit',
		hour12,
	});
}

export function downloadIcs(event) {
	const uid =
		'bookpress-' + event.id + '@' + (window.location.hostname || 'bookpress');
	const stamp = new Date()
		.toISOString()
		.replace(/[-:]/g, '')
		.split('.')[0] + 'Z';
	const start = event.start.replace(/[-:]/g, '').split('.')[0] + 'Z';
	const end = event.end.replace(/[-:]/g, '').split('.')[0] + 'Z';

	const lines = [
		'BEGIN:VCALENDAR',
		'VERSION:2.0',
		'PRODID:-//BookPress//Booking//EN',
		'BEGIN:VEVENT',
		'UID:' + uid,
		'DTSTAMP:' + stamp,
		'DTSTART:' + start,
		'DTEND:' + end,
		'SUMMARY:' + (event.title || 'Appointment'),
		'DESCRIPTION:' + (event.description || '').replace(/\n/g, '\\n'),
		'LOCATION:' + (event.location || ''),
		'END:VEVENT',
		'END:VCALENDAR',
	];

	const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = 'booking-' + event.id + '.ics';
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	URL.revokeObjectURL(url);
}

export function toIcsUtc(dateStr, timeStr, durationMin, timeZone) {
	const startMs = timeZone
		? wallTimeToUtcMs(dateStr, timeStr, timeZone)
		: (() => {
				const [y, m, d] = dateStr.split('-').map(Number);
				const [hh, mm] = timeStr.split(':').map(Number);
				return Date.UTC(y, m - 1, d, hh, mm || 0, 0);
			})();
	const endMs = startMs + (durationMin || 60) * 60000;
	const fmt = (ms) =>
		new Date(ms).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
	return { start: fmt(startMs), end: fmt(endMs) };
}
